import { ConnectQueryKeys } from '~/connect/api';
import {
  CrudOptions,
  useCrud,
  useMutation,
  UseMutationOptions,
  restrictApi,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IS2SFlowTemplate, IS2SFlowTemplatePayload } from '~/connect/types';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { CommonErrorCodesEnumType } from '~/common/types';

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type Options = Pick<
  CrudOptions<
    IS2SFlowTemplate,
    IS2SFlowTemplatePayload,
    IS2SFlowTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  toggleStatus?: UseMutationOptions<
    ISuccessApiResponse<IS2SFlowTemplate>,
    IErrorApiResponse<unknown, CommonErrorCodesEnumType>,
    string
  >;
};

export const useManageS2SFlowTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const endpoint = `/tenants/api/wtb/tenants/${tenantCode}/s2s-flow-templates`;

  const api = useCrud<
    Actions,
    IS2SFlowTemplate,
    IS2SFlowTemplatePayload,
    IS2SFlowTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.s2sFlowTemplates.base(),
      caller: callCoreApi,
      endpoint,
    },
    options,
  );

  const toggleStatus = useMutation({
    mutationFn: (id: string) =>
      callCoreApi<ISuccessApiResponse<IS2SFlowTemplate>>(
        `${endpoint}/${id}/toggle-status`,
        { method: 'PATCH' },
      ),
    keysToInvalidateOnSuccess: [ConnectQueryKeys.s2sFlowTemplates.base()],
    ...options.toggleStatus,
  });

  return restrictApi(options, {
    ...api,
    toggleStatus,
  });
};
